import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';

const StickyOffsetsContext = createContext(null);
const VARIABLES = [
  '--sticky-topbar-offset',
  '--sticky-form-header-offset',
  '--sticky-invoice-rail-offset',
  '--sticky-invoice-table-offset',
];

/** A single owner for the measured heights used by billing's stacked sticky layers. */
export function StickyOffsetsProvider({ children }) {
  const [heights, setHeights] = useState({});

  const register = useCallback((name, element) => {
    if (!element) return () => {};
    const measure = () => {
      const height = element.offsetHeight;
      setHeights((current) => current[name] === height ? current : { ...current, [name]: height });
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(element);
    return () => {
      observer?.disconnect();
      setHeights((current) => {
        if (!(name in current)) return current;
        const next = { ...current };
        delete next[name];
        return next;
      });
    };
  }, []);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const rem = parseFloat(getComputedStyle(root).fontSize) || 16;
    // El pill de la TopBar empieza a 0,75rem; el resto del hueco deja aire.
    const topbar = heights.topbar ? heights.topbar + 2 * rem : 5 * rem;
    const formHeader = topbar - 0.5 * rem;
    const offsets = [
      topbar,
      formHeader,
      formHeader + (heights.invoiceHeader || 0) + 0.5 * rem,
      topbar + (heights.invoiceToolbar || 0),
    ];
    VARIABLES.forEach((name, index) => root.style.setProperty(name, `${offsets[index]}px`));
    return () => VARIABLES.forEach((name) => root.style.removeProperty(name));
  }, [heights]);

  const value = useMemo(() => ({ register }), [register]);
  return <StickyOffsetsContext.Provider value={value}>{children}</StickyOffsetsContext.Provider>;
}

/** Register a mounted layer; outside the normal app layout CSS fallbacks apply. */
export function useStickyLayer(name, ref) {
  const context = useContext(StickyOffsetsContext);
  useEffect(() => context?.register(name, ref.current), [context, name, ref]);
}
